import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {ArrowLeft} from "lucide-react";
import {get} from "lodash";
import React, {useEffect} from "react";
import {Link, useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {curriculumSessionList} from "business/curriculumSession";
import {groupSessionsByTheme, hasThemes, orderByTheme} from "business/curriculumTheme";
import {NAV_BAR_HEIGHT} from "common/helpers";
import {color, space, type} from "common/tokens";
import SectionHeader from "components/redesign/SectionHeader";
import SessionCard from "components/redesign/SessionCard";

const useStyles = makeStyles(theme => ({
  page: {
    background: color.SURFACE,
    minHeight: `calc(100vh - ${NAV_BAR_HEIGHT})`,
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '24px 32px 64px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      padding: '16px 16px 48px',
    },
  },
  backLink: {
    ...type.body,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: color.TEXT_MUTED,
    textDecoration: 'none',
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: '18px 0 0',
  },
  helpText: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '8px 0 0',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    marginTop: 40,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  // auto-fill at a 250px minimum lands on four ~259px columns inside the
  // container, close to the ~282px the cards are drawn at in the design. The
  // design's own grid is hand-placed at varying widths, so it is not copied.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    columnGap: 20,
    rowGap: 25,
    alignItems: 'start',
  },
  // Without themes the page is one flat list, so the cards keep a fixed three
  // to a row rather than filling the container with narrower columns.
  gridFlat: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    },
  },
}));

const CurriculumMilestones = ({sessions, selfPicture, initMentorshipSessions}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const history = useHistory();

  useEffect(() => {
    initMentorshipSessions();
  }, [initMentorshipSessions]);

  const statuses = curriculumSessionList(
    get(sessions, 'ongoingSessions'),
    get(sessions, 'allSessions')
  );

  // A curriculum has either themes or sessions attached directly, never both.
  // Without themes there are no milestones to show, so the page becomes a flat
  // list of session cards under a plainer title.
  const themed = hasThemes(statuses);
  const groups = themed
    ? groupSessionsByTheme(statuses)
    : [{id: 'all', title: null, banner: null, sessions: orderByTheme(statuses)}];

  return (
    <div className={classes.page}>
      <div className={`${classes.container} ui-enter`}>
        <Link to={'/mentorship'} className={classes.backLink}>
          <ArrowLeft size={16} strokeWidth={2}/>
          {t('mentorship')}
        </Link>

        <h1 className={classes.title}>
          {themed ? t('curriculumMilestones') : t('curriculumSessions')}
        </h1>
        {themed && <p className={classes.helpText}>{t('milestonesHelpText')}</p>}

        <div className={classes.sections}>
          {groups.map(group => (
            <section className={classes.section} key={group.id}>
              {group.title && <SectionHeader title={group.title} banner={group.banner}/>}
              <div className={classNames(classes.grid, !themed && classes.gridFlat)}>
                {group.sessions.map(sessionStatus => (
                  <SessionCard
                    key={get(sessionStatus, 'session.id')}
                    sessionStatus={sessionStatus}
                    selfPicture={selfPicture}
                    onOpen={() => history.push(
                      `/mentorship/${get(sessionStatus, 'session.id')}/summary`,
                      {from: 'milestones'},
                    )}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CurriculumMilestones;
